import {
  transformAuthenticationOptions,
  serializeAuthenticationCredential,
} from "./webauthnHelpers";
import { finalizeSocialLoginSession } from "./socialAuthHelpers";

const apiBaseUrl = process.env.REACT_APP_API_DOMAIN;

/** Public endpoint fetch: no CSRF header, cookies included for the Knox session. */
async function postJson(path, body) {
  const response = await fetch(`${apiBaseUrl}${path}`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify(body),
  });

  let data = null;
  try {
    data = await response.json();
  } catch {
    data = null;
  }

  return { response, data };
}

/**
 * Discoverable-credential passkey login: fetch options, run the platform
 * prompt, verify, then finalize the session via `finalizeSocialLoginSession`.
 *
 * @returns {Promise<{ok: true} | {ok: false, notice: string} | {ok: false, error: string}>}
 */
export async function loginWithPasskey() {
  const { response: optionsResponse, data: options } = await postJson(
    "/auth/passkeys/authentication/options",
    {}
  );

  if (!optionsResponse.ok) {
    if (optionsResponse.status === 403) {
      return {
        ok: false,
        notice:
          "Passkey sign-in isn't available right now. Use your email and password.",
      };
    }
    return {
      ok: false,
      error: "Could not start passkey sign-in. Please try again.",
    };
  }

  let credential;
  try {
    credential = await navigator.credentials.get(
      transformAuthenticationOptions(options)
    );
  } catch (error) {
    if (error?.name === "NotAllowedError") {
      return {
        ok: false,
        notice:
          "No passkey was selected. Sign in with your email and password, then create one under Security.",
      };
    }
    return {
      ok: false,
      error: "Could not complete the passkey prompt. Please try again.",
    };
  }

  if (!credential) {
    return {
      ok: false,
      notice:
        "No passkey was selected. Sign in with your email and password, then create one under Security.",
    };
  }

  const { response: verifyResponse, data: verifyData } = await postJson(
    "/auth/passkeys/authentication/verify",
    { credential: serializeAuthenticationCredential(credential) }
  );

  if (!verifyResponse.ok) {
    return {
      ok: false,
      notice:
        "That passkey couldn't be used. Sign in with your email and password, then create a new passkey under Security.",
    };
  }

  await finalizeSocialLoginSession(verifyData);
  return { ok: true };
}
